import { Component, DestroyRef, ElementRef, NgZone, afterNextRender, inject, output, viewChild } from '@angular/core';
import { environment } from '../../../../environments/environment';

/**
 * Cloudflare Turnstile, checked by the API on `POST /v1/register`. Bots opened merchant accounts
 * through the registration form on 31 October 2026.
 *
 * A token is single-use, so the widget is reset after every failed submit.
 */
const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=onTurnstileLoad';

interface TurnstileApi {
  render(container: HTMLElement, options: Record<string, unknown>): string;
  reset(widgetId: string): void;
  remove(widgetId: string): void;
}

const w = window as unknown as { turnstile?: TurnstileApi; onTurnstileLoad?: () => void };
let scriptLoaded: Promise<void> | undefined;

function loadTurnstile(): Promise<void> {
  scriptLoaded ??= new Promise<void>((resolve, reject) => {
    w.onTurnstileLoad = () => resolve();
    const script = document.createElement('script');
    script.src = SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      scriptLoaded = undefined;
      reject();
    };
    document.head.appendChild(script);
  });
  return scriptLoaded;
}

@Component({
  selector: 'app-turnstile',
  template: '<div #container class="turnstile"></div>',
  styles: ['.turnstile { min-height: 65px; margin-bottom: 10px; }'],
})
export class Turnstile {
  static readonly enabled = !!environment.TURNSTILE_SITE_KEY;

  readonly token = output<string | null>();

  private readonly container = viewChild.required<ElementRef<HTMLElement>>('container');
  private readonly zone = inject(NgZone);
  private widgetId: string | undefined;
  private destroyed = false;

  constructor() {
    afterNextRender(() => this.render());
    inject(DestroyRef).onDestroy(() => {
      this.destroyed = true;
      if (this.widgetId !== undefined) {
        w.turnstile?.remove(this.widgetId);
      }
    });
  }

  reset() {
    this.emit(null);
    if (this.widgetId !== undefined) {
      w.turnstile?.reset(this.widgetId);
    }
  }

  private render() {
    loadTurnstile().then(
      () => {
        if (this.destroyed || !w.turnstile) {
          return;
        }
        this.widgetId = w.turnstile.render(this.container().nativeElement, {
          sitekey: environment.TURNSTILE_SITE_KEY,
          action: 'register',
          callback: (token: string) => this.emit(token),
          'expired-callback': () => this.emit(null),
          'error-callback': () => this.emit(null),
        });
      },
      () => this.emit(null),
    );
  }

  private emit(token: string | null) {
    this.zone.run(() => this.token.emit(token));
  }
}
